import { addMilliseconds } from 'date-fns';

import { getNTPTime } from './ntp-socket';

const intervalPeriod = 1_000 * 60;

class NTPClient {
  static readonly defaultNTPServerHost = 'time.google.com';
  static readonly defaultNTPServerPort = 123;

  #ntpNow: Date | undefined;

  get ntpNow() {
    this.#ensureStarted();

    if (!this.#ntpNow) return undefined;

    const updatedOffsetMilliseconds = Date.now() - this.#lastUpdateTime.getTime();
    return addMilliseconds(this.#ntpNow, updatedOffsetMilliseconds);
  }

  #lastUpdateTime: Date = new Date();

  #intervalID: NodeJS.Timeout | undefined;

  #started = false;

  public constructor(
    public ntpServerHost = NTPClient.defaultNTPServerHost,
    public ntpServerPort = NTPClient.defaultNTPServerPort,
  ) {}

  /**
   * Starts the NTP sync lazily.
   *
   * `getNTPTime` is a Server Action (`'use server'`). Invoking it synchronously
   * during module evaluation / render (e.g. from the singleton constructor)
   * dispatches a router action before the Next.js App Router is initialized,
   * which throws "Router action dispatched before initialization". Deferring the
   * first sync to a macrotask guarantees it runs after bootstrap, and the clock
   * safely falls back to system time until the first sync completes.
   */
  #ensureStarted() {
    if (this.#started) return;

    // `getNTPTime` is a client-invoked Server Action (RPC). Only run the sync
    // loop in the browser; on the server the clock falls back to system time.
    if (typeof window === 'undefined') return;

    this.#started = true;
    setTimeout(() => {
      void this.#scheduleSyncTime();
    }, 0);
  }

  get #shouldSyncTime() {
    if (!this.#ntpNow) return true;
    const now = new Date();
    return now.getTime() > this.#lastUpdateTime.getTime() + intervalPeriod;
  }

  async #scheduleSyncTime() {
    if (this.#intervalID) clearInterval(this.#intervalID);
    await this.syncTime();
    this.#intervalID = setInterval(async () => {
      if (!this.#shouldSyncTime) return;
      await this.syncTime();
    }, 1_000 * 30);
  }

  async syncTime() {
    try {
      const ntpTime = await getNTPTime(this.ntpServerHost, this.ntpServerPort);
      if (!ntpTime) return;
      this.#ntpNow = new Date(ntpTime);
      this.#lastUpdateTime = new Date();
    } catch {
      // do nothing
    }
  }
}

export default NTPClient;
